import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  forwardRef,
  inject,
} from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { WidgetConfigAccessor } from '../services/widget-config.accessor';
import {
  BaseCssProperties,
  BorderRadiusKeys,
  TagColorConfig,
  TypographyKeys,
  UiZoneWidgetAttributeConfig,
  baseCssConfigStyle,
} from '@finxone-platform/shared/sys-config-types';

@Component({
  standalone: false,
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'finx-tag',
  templateUrl: './finx-tag.component.html',
  styleUrls: ['./finx-tag.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FinxTagComponent),
      multi: true,
    },
  ],
})
export class FinxTagComponent implements OnInit, AfterViewInit {
  private configService = inject(WidgetConfigAccessor);
  private elementRef = inject(ElementRef);
  private cdr = inject(ChangeDetectorRef);

  @Input() label: string;
  @Input() config: UiZoneWidgetAttributeConfig;
  @Input() isActive: boolean;
  @Input() hideCloseIcon = false;
  @Input() value: string;
  @Output() filterChange = new EventEmitter<{ name: string; value: string; isActive: boolean }>();
  public filterPillConfig: any;
  public iconConfig: any;
  public colorConfig: TagColorConfig;
  public icon = 'alert-error-icon'; // default icon
  public paddingStyle: baseCssConfigStyle;
  public iconContentStyle: baseCssConfigStyle;
  public colourStyle: baseCssConfigStyle;
  public borderStyle: baseCssConfigStyle;
  private shadowStyle: baseCssConfigStyle;
  private sizeStyle: baseCssConfigStyle;
  public borderClass: string;
  public textStyleClass: TypographyKeys | '' = TypographyKeys.BUTTON_TEXT;

  get isGlobalStylingEnabled(): boolean {
    return this.config?.['filterPillConfig']?.['globalStyling'] ?? false;
  }

  ngOnInit(): void {
    if (this.config?.['filterPillConfig']) {
      this.filterPillConfig = this.config['filterPillConfig'];
      this.iconConfig = this.filterPillConfig['iconConfig'];
      this.icon = this.iconConfig.icon;
      if (this.isGlobalStylingEnabled) {
        this.textStyleClass = '';
        return;
      }
      this.colorConfig = this.filterPillConfig['colorConfig'];
      this.textStyleClass = this.filterPillConfig['textStyleClass'] ?? TypographyKeys.BUTTON_TEXT;
      this.iconContentStyle = this.configService.getSpecificBaseCssStyle(this.iconConfig, [
        BaseCssProperties.MARGIN,
        BaseCssProperties.COLOR,
        BaseCssProperties.ICON_COLOR,
        BaseCssProperties.ICON_SIZE,
      ]);
      this.sizeStyle = this.configService.getSpecificBaseCssStyle(this.filterPillConfig, [
        BaseCssProperties.DIMENSIONS,
      ]);
      delete this.sizeStyle['width'];
      this.borderStyle = this.filterPillConfig['borderConfig'];
      this.borderClass =
        this.formatBorderRadius(this.filterPillConfig['borderConfig']?.['borderRadiusClass']) ??
        BorderRadiusKeys.NONE;
      this.shadowStyle = {
        'box-shadow': `${this.filterPillConfig.css_config?.shadowOffsetX} ${this.filterPillConfig.css_config?.shadowOffsetY} ${this.filterPillConfig.css_config?.shadowBlur} ${this.filterPillConfig.css_config?.shadowSpread} ${this.filterPillConfig.css_config?.shadowColor}`,
      };
      this.paddingStyle = this.configService.getSpecificBaseCssStyle(this.filterPillConfig, [
        BaseCssProperties.PADDING,
      ]);
    }
  }
  ngAfterViewInit(): void {
    this.applyGlobalIcon();
    this.updateTagStyles();
  }

  /**
   * CSS can't choose which svg a mat-icon renders, so the theme publishes the icon name as `--tag-icon`
   * and a linked pill reads it back. Without it (theme css not yet saved with filter pills styling) the
   * widget's own icon is kept.
   */
  private applyGlobalIcon(): void {
    if (!this.isGlobalStylingEnabled) return;
    const pill = this.elementRef.nativeElement.querySelector('.finx-tag');
    const themeIcon = pill ? getComputedStyle(pill).getPropertyValue('--tag-icon').trim() : '';
    if (!themeIcon) return;
    this.icon = themeIcon.replace(/^["']|["']$/g, '');
    this.cdr.detectChanges();
  }

  private updateTagStyles(): void {
    if (this.isGlobalStylingEnabled) return;
    const styles = this.getComputedStyles();
    Object.entries(styles).forEach(([key, value]) => {
      document.documentElement.style.setProperty(`--tag-${key}`, value);
    });
  }

  private getComputedStyles() {
    const marginStyles = this.configService.getSpecificBaseCssStyle(this.filterPillConfig, [
      BaseCssProperties.MARGIN,
    ]);
    return {
      'active-bg-color': this.colorConfig?.active?.['background-color'] ?? '#EDEDED',
      'active-color': this.colorConfig?.active?.textColor?.color ?? 'var(--color-on-background)',
      'active-border-color': this.colorConfig?.active?.['border-color'] ?? 'unset',
      'inactive-bg-color': this.colorConfig?.idle?.['background-color'] ?? '#EDEDED',
      'inactive-color': this.colorConfig?.idle?.textColor?.color ?? 'var(--color-on-background)',
      'inactive-border-color': this.colorConfig?.idle?.['border-color'] ?? 'unset',
      height: this.sizeStyle?.height ?? 'auto',
      'margin-left': marginStyles['margin-left'] ?? '0',
      'margin-right': marginStyles['margin-right'] ?? '0',
      'padding-left': this.paddingStyle?.['padding-left'] ?? '0',
      'padding-right': this.paddingStyle?.['padding-right'] ?? '0',
      'box-shadow': this.shadowStyle?.['box-shadow'] ?? 'none',
      'border-bottom-left-radius':
        this.filterPillConfig?.['borderConfig']?.['border-bottom-left-radius'] ?? '0',
      'border-bottom-right-radius':
        this.filterPillConfig?.['borderConfig']?.['border-bottom-right-radius'] ?? '0',
      'border-top-left-radius': this.filterPillConfig?.['borderConfig']?.['border-top-left-radius'] ?? '0',
      'border-top-right-radius': this.filterPillConfig?.['borderConfig']?.['border-top-right-radius'] ?? '0',
      'border-left-width': this.filterPillConfig?.['borderConfig']?.['border-left-width'] ?? '0',
      'border-right-width': this.filterPillConfig?.['borderConfig']?.['border-right-width'] ?? '0',
      'border-top-width': this.filterPillConfig?.['borderConfig']?.['border-top-width'] ?? '0',
      'border-bottom-width': this.filterPillConfig?.['borderConfig']?.['border-bottom-width'] ?? '0',
      'border-style': this.filterPillConfig?.['borderConfig']?.['border-style'] ?? 'solid',
    };
  }

  public tagClicked() {
    this.isActive = !this.isActive;
    this.updateTagStyles();
    this.filterChange.emit({
      name: this.label,
      value: this.value,
      isActive: this.isActive,
    });
  }

  private formatBorderRadius(radius: string): string {
    if (radius !== BorderRadiusKeys.NONE) {
      radius = radius.replace('radius', 'rad');
    }
    return radius;
  }
}
